import { useMemo } from "react";
import { cn } from "@/lib/cn";
import { fmtMoneyExact } from "@/lib/cost";
import { EBITDA_DEFINITION, annualBenefitTotal, asRealized, inYearBenefitTotal } from "@/lib/benefit";
import type { Project } from "@/types/project";

/**
 * The board scoreboard — four numbers that set the meeting agenda.
 * Shared by /executive and /top-projects (Brett, 2026-09-18: "both
 * pages") so the two can never disagree: same rows in, same math.
 *
 * `rows` = the non-complete projects the host page is about: every
 * band on /top-projects, the Top 10 only on /executive (Brett,
 * 2026-09-21). `scopeLabel` names that scope in the card copy. The At
 * Risk card is a FILTER toggle the host applies to its own surface —
 * bands on the board, Gantt rows on the exec page.
 *
 * 2026-09-22 (Brett): "In Delivery" retired — it read 9 of 10 every
 * week and said nothing. Cards are now On Track / At Risk / Go-Lives /
 * Benefit, every count as "N of total" so nobody adds them up.
 *
 * 2026-11-04 (Brett): the board remembers the Top 10 was to deliver
 * ~$2.3M. A project that COMPLETES keeps its EBITDA on the card —
 * `delivered` rows add to the benefit numbers (shown as delivered ·
 * in flight) but never to the health counts, which stay about live work.
 */

const YEAR = new Date().getFullYear();

/** A paused project is a decision the board made, not a delivery
 * problem — it never counts as at risk or on track (Brett, 2026-09-22). */
export function isPostponed(p: Project): boolean {
  return (p.status ?? "").toLowerCase() === "postponed";
}

export function isAtRisk(p: Project): boolean {
  return !isPostponed(p) && ["red", "yellow"].includes((p.health ?? "").toLowerCase());
}

/**
 * "Delivered" projects — the ones whose EBITDA stays in the totals after
 * they finish (Brett, 2026-11-04). A project counts when BOTH hold:
 *   1. its status is Complete, and
 *   2. it still carries a decided band (Top 10, Just Do, Next up, Paused).
 * New / unscoped is the intake queue, not committed work, and a project
 * with no band was never on the board — neither counts. Pass `band` to
 * keep one band only (the Executive page and each band's own total).
 */
export function deliveredBoardProjects(projects: Project[], band?: string): Project[] {
  return projects.filter((project) => {
    const isComplete = (project.status ?? "").toLowerCase() === "complete";
    const projectBand = project.priority_tier;
    if (!isComplete || !projectBand || projectBand === "unscoped") return false;
    return band ? projectBand === band : true;
  });
}

export function isOnTrack(p: Project): boolean {
  return !isPostponed(p) && (p.health ?? "").toLowerCase() === "green";
}

/**
 * The three health cards partition `rows` exactly: on track + at risk +
 * not assessed + postponed = total, so "6 of 10" and "4 of 10" can be
 * read together without the board doing arithmetic. Go-lives overlap
 * with health on purpose (a red project can still go live next month).
 */
export function computeBoardStats(rows: Project[], year = YEAR, delivered: Project[] = []) {
  const total = rows.length;
  const postponed = rows.filter(isPostponed).length;
  const onTrack = rows.filter(isOnTrack).length;
  const atRisk = rows.filter(isAtRisk).length;
  const now = new Date();
  const in30 = new Date();
  in30.setDate(in30.getDate() + 30);
  const goLives30 = rows.filter((p) => {
    if (!p.end_date) return false;
    const d = new Date(p.end_date);
    return d >= now && d <= in30;
  }).length;
  return {
    total,
    onTrack,
    atRisk,
    notAssessed: total - onTrack - atRisk - postponed,
    postponed,
    goLives30,
    benefit: annualBenefitTotal(rows) + annualBenefitTotal(delivered),
    benefitInYear: inYearBenefitTotal(rows, year) + inYearBenefitTotal(asRealized(delivered), year),
    benefitDelivered: annualBenefitTotal(delivered),
    benefitInFlight: annualBenefitTotal(rows),
    deliveredCount: delivered.length,
    year,
  };
}

export function BoardKpiCards({
  rows,
  atRiskOnly,
  onToggleAtRisk,
  scopeLabel = "on the board",
  delivered = [],
}: {
  rows: Project[];
  atRiskOnly: boolean;
  onToggleAtRisk: () => void;
  /** Where the numbers come from, e.g. "on the board" / "in the Top 10". */
  scopeLabel?: string;
  /** Completed projects in the same scope: their EBITDA stays on the card. */
  delivered?: Project[];
}) {
  const stats = useMemo(() => computeBoardStats(rows, YEAR, delivered), [rows, delivered]);
  const gaps = [
    stats.notAssessed > 0 && `${stats.notAssessed} not assessed`,
    stats.postponed > 0 && `${stats.postponed} postponed`,
  ].filter(Boolean);
  const ofTotal = <span className="text-lg font-semibold text-ink-subtle"> of {stats.total}</span>;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="board-kpis">
      <div className="relative overflow-hidden rounded-2xl border bg-surface p-5 pl-6 shadow-sm ring-1 ring-line">
        <div className="absolute bottom-0 left-0 top-0 w-[5px] bg-emerald-400" />
        <div className="text-xs font-bold uppercase tracking-wider text-ink-muted">On Track</div>
        <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-ink" data-testid="kpi-on-track">
          {stats.onTrack}
          {ofTotal}
        </div>
        <div className="mt-2 text-xs text-ink-muted" data-testid="kpi-on-track-gaps">
          {gaps.length > 0 ? gaps.join(" · ") : `Green health ${scopeLabel}`}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggleAtRisk}
        data-card="at-risk"
        className={cn(
          // flex + justify-start: a <button> centers its content vertically, which
          // showed once the EBITDA card grew a line and stretched the row.
          "relative flex flex-col justify-start overflow-hidden rounded-2xl border bg-surface p-5 pl-6 text-left shadow-sm ring-1 transition-all",
          atRiskOnly ? "ring-2 ring-amber-400 border-amber-300 dark:border-amber-500/40" : "ring-line hover:shadow-md",
        )}
        title={atRiskOnly ? "Show all rows" : "Show only red/yellow rows"}
      >
        <div className={cn("absolute bottom-0 left-0 top-0 w-[5px]", stats.atRisk > 0 ? "bg-amber-400" : "bg-emerald-400")} />
        <div className="text-xs font-bold uppercase tracking-wider text-ink-muted">At Risk</div>
        <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-ink" data-testid="kpi-at-risk">
          {stats.atRisk}
          {ofTotal}
        </div>
        <div className="mt-2 text-xs text-ink-muted">
          {atRiskOnly ? "Filtering — click to show all" : "Red or yellow health — click to focus the meeting"}
        </div>
      </button>
      <div className="relative overflow-hidden rounded-2xl border bg-surface p-5 pl-6 shadow-sm ring-1 ring-line">
        <div className="absolute bottom-0 left-0 top-0 w-[5px] bg-emerald-400" />
        <div className="text-xs font-bold uppercase tracking-wider text-ink-muted">Go-Lives · 30 Days</div>
        <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-ink" data-testid="kpi-go-lives">
          {stats.goLives30}
          {ofTotal}
        </div>
        <div className="mt-2 text-xs text-ink-muted">Committed go-live dates {scopeLabel}, next month</div>
      </div>
      <div className="relative overflow-hidden rounded-2xl border bg-surface p-5 pl-6 shadow-sm ring-1 ring-line">
        <div className="absolute bottom-0 left-0 top-0 w-[5px] bg-emerald-500" />
        <div className="w-fit cursor-help text-xs font-bold uppercase tracking-wider text-ink-muted underline decoration-dotted underline-offset-2" title={EBITDA_DEFINITION} data-testid="kpi-benefit-label">Annual EBITDA</div>
        <div className="mt-2 text-4xl font-extrabold tabular-nums leading-none text-emerald-700 dark:text-emerald-300" data-testid="kpi-benefit">{fmtMoneyExact(stats.benefit)}</div>
        {stats.deliveredCount > 0 && (
          <div className="mt-1 text-xs text-ink-muted" data-testid="kpi-benefit-split" title="Completed projects keep their EBITDA on the card — the Top 10 commitment doesn't shrink when a project delivers">
            <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{fmtMoneyExact(stats.benefitDelivered)}</span> delivered ·{" "}
            <span className="font-semibold tabular-nums">{fmtMoneyExact(stats.benefitInFlight)}</span> in flight
          </div>
        )}
        <div className="mt-2 text-xs text-ink-muted" data-testid="in-year-benefit">
          <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{fmtMoneyExact(stats.benefitInYear)}</span>{" "}
          captured in {stats.year} {scopeLabel}, prorated from each go-live date
        </div>
      </div>
    </div>
  );
}
